import type { ComponentType, ReactElement, ReactNode } from 'react';

export type QueryValue = string | string[] | undefined;
export type RouterQuery = Record<string, QueryValue>;

export interface UrlObject {
	pathname?: string | null;
	query?: RouterQuery;
	hash?: string;
}

export interface NavigationOptions {
	locale?: string | false;
	shallow?: boolean;
	scroll?: boolean;
}

export interface RouteEventBus {
	on(event: string, listener: (...args: any[]) => void): void;
	off(event: string, listener: (...args: any[]) => void): void;
	emit(event: string, ...args: any[]): void;
}

export interface ClientRouterPopState {
	as: string;
	options: Record<string, unknown>;
	state: unknown;
	url: string;
}

/**
 * Browser traversal has three distinct outcomes:
 * - Continue lets React Router and page listeners observe the new location.
 * - HandleLocally keeps React Router mounted while page listeners synchronize local UI.
 * - Block prevents every listener from observing a traversal that is being restored first.
 */
export const ClientRouterPopStateDecision = {
	Block: 'block',
	Continue: 'continue',
	HandleLocally: 'handle-locally'
} as const;

export type ClientRouterPopStateDecision = (typeof ClientRouterPopStateDecision)[keyof typeof ClientRouterPopStateDecision];

export interface ClientRouter {
	asPath: string;
	basePath: string;
	events: RouteEventBus;
	isReady: boolean;
	locale: string;
	locales: string[];
	pageResetKey: number;
	pathname: string;
	query: RouterQuery;
	back(): void;
	beforePopState(callback: (state: ClientRouterPopState) => ClientRouterPopStateDecision): void;
	prefetch(url: string): Promise<void>;
	push(url: string | UrlObject, as?: string, options?: NavigationOptions): Promise<boolean>;
	reload(): void;
	replace(url: string | UrlObject, as?: string, options?: NavigationOptions): Promise<boolean>;
	resolveHref(url: string | UrlObject, options?: NavigationOptions): string;
}

export type PageComponent<P = Record<string, unknown>> = ComponentType<P> & {
	bypassPageInitialization?: boolean;
	getLayout?: (page: ReactElement) => ReactNode;
};

export interface PageModule {
	default: PageComponent<any>;
}

export interface RouteDefinition {
	file: string;
	importPage: () => Promise<PageModule>;
	routePattern: string;
	reactPath: string;
	splatParam?: string;
}
